import { useCallback, useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import "./AddClientContactsDialog.css";

const initialFormData = {
  name: "",
  position: "",
  phone: "",
  email: "",
};

const initialErrors = {
  name: "",
  position: "",
  phone: "",
  email: "",
  submit: "",
};

export default function AddClientContacts({
  isOpen,
  onClose,
  onAddContact,
  clientId,
}) {
  const [contactData, setContactData] = useState(initialFormData);
  const [errors, setErrors] = useState(initialErrors);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const disabledAll = useMemo(() => isSubmitting, [isSubmitting]);

  // Resetează complet formularul și erorile la valorile inițiale.
  // Este folosit atât la deschiderea dialogului, cât și la închiderea lui.
  const resetForm = useCallback(() => {
    setContactData(initialFormData);
    setErrors(initialErrors);
    setIsSubmitting(false);
    setSubmitted(false);
  }, []);

  // Când dialogul se deschide, formularul este resetat pentru a porni mereu cu date curate.
  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  // Închide dialogul și resetează starea internă.
  // onClose vine din componenta părinte.
  const forceClose = useCallback(() => {
    resetForm();
    onClose?.();
  }, [onClose, resetForm]);

  // Închidere controlată a dialogului.
  // Nu permite închiderea cât timp cererea este în curs.
  const handleClose = useCallback(() => {
    if (disabledAll) return;
    forceClose();
  }, [disabledAll, forceClose]);

  // Actualizează valorile inputurilor în state
  // și curăță erorile pentru câmpul modificat.
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;

    setContactData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
      submit: "",
    }));
  }, []);

  // Validează toate câmpurile formularului:
  // - name și position obligatorii
  // - phone obligatoriu și valid
  // - email obligatoriu și în format corect
  // În același timp, actualizează obiectul errors pentru afișarea în UI.
  const validateForm = useCallback(() => {
    const newErrors = { ...initialErrors };

    if (!contactData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!contactData.position.trim()) {
      newErrors.position = "Position is required";
    }

    const normalizedPhone = contactData.phone.replace(/\s/g, "");
    if (!contactData.phone.trim()) {
      newErrors.phone = "Phone is required";
    } else if (!/^\+?[0-9]\d{1,19}$/.test(normalizedPhone)) {
      newErrors.phone = "Please enter a valid phone number";
    }

    if (!contactData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactData.email.trim())) {
      newErrors.email = "Invalid email format";
    }

    setErrors((prev) => ({
      ...prev,
      ...newErrors,
      submit: "",
    }));

    return (
      !newErrors.name &&
      !newErrors.position &&
      !newErrors.phone &&
      !newErrors.email
    );
  }, [contactData]);

  // Funcția principală de submit:
  // 1. marchează formularul ca trimis
  // 2. validează datele introduse
  // 3. construiește obiectul contactului
  // 4. atașează clientId dacă există
  // 5. trimite contactul mai departe prin onAddContact
  // 6. închide dialogul la succes
  // În caz de eroare, afișează mesajul în secțiunea submit.
  const handleSubmit = useCallback(async () => {
    setSubmitted(true);
    setErrors((prev) => ({ ...prev, submit: "" }));

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const contactToAdd = {
        name: contactData.name.trim(),
        position: contactData.position.trim(),
        phone: contactData.phone.trim(),
        email: contactData.email.trim(),
        contact_status: "Active",
      };

      if (clientId) {
        contactToAdd.client = clientId;
      }

      await onAddContact(contactToAdd);

      forceClose();
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        submit:
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          error?.message ||
          "Error adding contact",
      }));
    } finally {
      setIsSubmitting(false);
    }
  }, [validateForm, contactData, clientId, onAddContact, forceClose]);

  // Variabile ajutătoare pentru a afișa mesajele de eroare
  // doar după ce utilizatorul a încercat submit-ul.
  const showNameError = submitted && !!errors.name;
  const showPositionError = submitted && !!errors.position;
  const showPhoneError = submitted && !!errors.phone;
  const showEmailError = submitted && !!errors.email;

  return (
    <Dialog
      open={isOpen}
      onClose={disabledAll ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
      className="add-client-contacts-dialog"
    >
      {/* Header-ul dialogului */}
      <DialogTitle className="accd-title">
        <span className="accd-header-title">Add Contact Person</span>
      </DialogTitle>

      <DialogContent dividers className="accd-content">
        {/* Secțiunea pentru numele persoanei de contact */}
        <div className="accd-form-group">
          <label className="accd-label" htmlFor="add-client-contact-name">
            Name <span style={{ color: "red" }}>*</span>
          </label>
          <input
            id="add-client-contact-name"
            type="text"
            name="name"
            value={contactData.name}
            onChange={handleChange}
            disabled={disabledAll}
            className={`accd-input ${showNameError ? "accd-input-error" : ""}`}
            placeholder="Enter contact name"
          />
          {showNameError && (
            <div className="accd-field-error">{errors.name}</div>
          )}
        </div>

        {/* Secțiunea pentru poziția/functia contactului */}
        <div className="accd-form-group">
          <label className="accd-label" htmlFor="add-client-contact-position">
            Position <span style={{ color: "red" }}>*</span>
          </label>
          <input
            id="add-client-contact-position"
            type="text"
            name="position"
            value={contactData.position}
            onChange={handleChange}
            disabled={disabledAll}
            className={`accd-input ${
              showPositionError ? "accd-input-error" : ""
            }`}
            placeholder="Enter position"
          />
          {showPositionError && (
            <div className="accd-field-error">{errors.position}</div>
          )}
        </div>

        {/* Secțiunea pentru numărul de telefon */}
        <div className="accd-form-group">
          <label className="accd-label" htmlFor="add-client-contact-phone">
            Phone <span style={{ color: "red" }}>*</span>
          </label>
          <input
            id="add-client-contact-phone"
            type="text"
            name="phone"
            value={contactData.phone}
            onChange={handleChange}
            disabled={disabledAll}
            className={`accd-input ${showPhoneError ? "accd-input-error" : ""}`}
            placeholder="Enter phone number"
          />
          {showPhoneError && (
            <div className="accd-field-error">{errors.phone}</div>
          )}
        </div>

        {/* Secțiunea pentru email */}
        <div className="accd-form-group">
          <label className="accd-label" htmlFor="add-client-contact-email">
            Email <span style={{ color: "red" }}>*</span>
          </label>
          <input
            id="add-client-contact-email"
            type="email"
            name="email"
            value={contactData.email}
            onChange={handleChange}
            disabled={disabledAll}
            className={`accd-input ${showEmailError ? "accd-input-error" : ""}`}
            placeholder="Enter email address"
          />
          {showEmailError && (
            <div className="accd-field-error">{errors.email}</div>
          )}
        </div>

        {/* Statusul este afișat informativ și este fix pe "Active" */}
        <div className="accd-form-group">
          <label className="accd-label">Status</label>
          <input
            type="text"
            value="Active"
            readOnly
            disabled
            className="accd-input"
          />
        </div>

        {/* Mesaj general de eroare pentru submit */}
        {errors.submit && (
          <div className="accd-submit-error">{errors.submit}</div>
        )}
      </DialogContent>

      <DialogActions className="accd-actions">
        {/* Buton de închidere/cancel */}
        <Button
          onClick={handleClose}
          disabled={disabledAll}
          className="accd-btn accd-btn-cancel"
        >
          Cancel
        </Button>

        {/* Butonul principal de adăugare contact */}
        <Button
          onClick={handleSubmit}
          disabled={disabledAll}
          variant="contained"
          className="accd-btn accd-btn-create"
          startIcon={
            isSubmitting ? (
              <CircularProgress size={18} className="accd-spinner" />
            ) : null
          }
        >
          {isSubmitting ? "Adding..." : "Add Contact"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}